import { Banner, Button, LoadingIndicator, SlideToggle, YesNoDialog } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useState } from 'react';
import theme from './ProjectModules.module.scss';
import { BygrModule, BygrProject } from 'types';
import {
    getAvailableDocumentCategories,
    setDocumentCategoryHierarchy,
    setModule,
    transfereFDVFromAmToCM as transferFDVFromAmToCm,
} from 'services/projects';
import ProjectModulesUnitsModal from './ProjectModulesUnitsModal';
import { FormContainer } from 'components/Generic';
import ProjectSelectCategoriesModal from './ProjectSelectCategoriesModal';
import { useAsyncState } from 'utils/hooks';

interface ProjectModulesProps {
    project: BygrProject;
    reloadProject(): void;
}

const ProjectModules: React.FC<ProjectModulesProps> = ({ project, reloadProject }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const [showDialog, setShowDialog] = useState(false);
    const [showDialogCM, setShowDialogCM] = useState(false);
    const [showDialogQM, setShowDialogQM] = useState(false);
    const [showDialogSales, setShowDialogSales] = useState(false);
    const [showDialogFDV, setShowDialogFDV] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showSetCategoryModal, setShowSetCategoryModal] = useState(false);
    const [availableDocumentCategories, setAvalibleDocumentCategories] = useAsyncState(
        () => getAvailableDocumentCategories(project.id),
        [],
        null
    );

    const hasModule = (module: BygrModule) => module === (project.sellerModules & module);

    const toggleModule = async (module: BygrModule) => {
        const moduleIsActive = hasModule(module);
        if (
            module === BygrModule.Aftermarket &&
            !moduleIsActive &&
            availableDocumentCategories &&
            !availableDocumentCategories.hasLinkedDocuments
        ) {
            setShowCategoryModal(true);
        } else {
            await doSetModule(module);
        }
    };
    const doSetModule = async (module: BygrModule) => {
        const moduleIsActive = hasModule(module);
        await setModule(project.id, { isActive: !moduleIsActive, module });
        reloadProject();
    };
    const transfereFDV = async () => {
        setShowDialogFDV(false);
        setLoading(true);
        await transferFDVFromAmToCm(project.id);
        setLoading(false);
    };
    if (!availableDocumentCategories) return <LoadingIndicator />;
    return (
        <>
            <FormContainer className={theme.container} maxSize="800px">
                <h3>Change seller modules</h3>
                <div className="form columns-1 labels-20">
                    <SlideToggle
                        checked={hasModule(BygrModule.ChangeManagement)}
                        disabled={hasModule(BygrModule.ChangeManagement)}
                        onChange={() => setShowDialogCM(true)}>
                        Change management
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.Aftermarket)} onChange={() => setShowDialog(true)}>
                        Aftermarket
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.QualityManagement)} onChange={() => setShowDialogQM(true)}>
                        Quality management
                    </SlideToggle>
                    <SlideToggle checked={hasModule(BygrModule.Sales)} onChange={() => setShowDialogSales(true)}>
                        Sales
                    </SlideToggle>
                </div>
                <Button onClick={() => setModalIsOpen(true)} color="secondary" block>
                    Change buyer modules for residential units
                </Button>

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogCM}
                    onClose={() => setShowDialogCM(false)}
                    onAccept={() => toggleModule(BygrModule.ChangeManagement)}
                    body={`Are you sure you want to activate Change management for ${project.name}? This cannot be undone.`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialog}
                    onClose={() => setShowDialog(false)}
                    onAccept={() => toggleModule(BygrModule.Aftermarket)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.Aftermarket) ? 'deactivate' : 'activate'
                    } aftermarket for ${project.name}?`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogQM}
                    onClose={() => setShowDialogQM(false)}
                    onAccept={() => toggleModule(BygrModule.QualityManagement)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.QualityManagement) ? 'deactivate' : 'activate'
                    } QM for ${project.name}?`}
                />

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogSales}
                    onClose={() => setShowDialogSales(false)}
                    onAccept={() => toggleModule(BygrModule.Sales)}
                    body={`Are you sure you want to  ${
                        hasModule(BygrModule.Sales) ? 'deactivate' : 'activate'
                    } Sales for ${project.name}?`}
                />
                <ProjectModulesUnitsModal isOpen={modalIsOpen} projectId={project.id} toggle={() => setModalIsOpen(false)} />
            </FormContainer>

            <FormContainer className={theme.container} maxSize="800px">
                <h3>Transfer between modules</h3>
                <Button loading={loading} onClick={() => setShowDialogFDV(true)} color="secondary" block>
                    Transfer FDV from CM options to AM FDV
                </Button>

                <YesNoDialog
                    title="Are you sure"
                    show={showDialogFDV}
                    onClose={() => setShowDialogFDV(false)}
                    onAccept={() => transfereFDV()}
                    body={`Are you sure you want to transfer documents 
                    from CM Options to AM FDV for ${project.name}? This action cannot be undone! So make sure you have selected the right project.`}
                />
                <ProjectModulesUnitsModal isOpen={modalIsOpen} projectId={project.id} toggle={() => setModalIsOpen(false)} />
            </FormContainer>
            {hasModule(BygrModule.Aftermarket) && (
                <FormContainer className={theme.container} maxSize="800px">
                    <h3>Change document category hierarchy in AM</h3>
                    {availableDocumentCategories.hasLinkedDocuments && (
                        <Banner type="danger">
                            This will change the document category hierarchy and{' '}
                            <b>REMOVE ALL EXISTING DOCUMENT CATEGORIES</b>. This action cannot be undone.
                        </Banner>
                    )}
                    <Button onClick={() => setShowSetCategoryModal(true)} color="secondary" block>
                        Change document category hierarchy
                    </Button>
                </FormContainer>
            )}
            <ProjectSelectCategoriesModal
                isOpen={showCategoryModal}
                toogle={() => setShowCategoryModal(false)}
                onSave={async (hierarchyId) => {
                    await doSetModule(BygrModule.Aftermarket);
                    const data = await setDocumentCategoryHierarchy(project.id, hierarchyId);
                    setAvalibleDocumentCategories(data);
                    setShowCategoryModal(false);
                }}
                values={availableDocumentCategories.categories}
                selectedValue={availableDocumentCategories.selectedHierarchy}
            />
            <ProjectSelectCategoriesModal
                isOpen={showSetCategoryModal}
                toogle={() => setShowSetCategoryModal(false)}
                onSave={async (hierarchyId) => {
                    const data = await setDocumentCategoryHierarchy(project.id, hierarchyId);
                    setAvalibleDocumentCategories(data);
                    setShowSetCategoryModal(false);
                }}
                values={availableDocumentCategories.categories}
                selectedValue={availableDocumentCategories.selectedHierarchy}
            />
        </>
    );
};

export default ProjectModules;
